import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Users, X } from "lucide-react";

export type PreparorUser = {
  id?: string;
  user_id?: string;
  name?: string;
  role?: string;
};

/**
 * Typeahead user picker shared by TaskCreate and TaskEdit. The field is a
 * trigger that shows the current selection; clicking it opens a centered popup
 * (portalled to <body> so a transformed modal ancestor can't capture the fixed
 * overlay) with a search box and a scrollable list (~5 rows visible at a time).
 */
export function PreparorSelect({
  value,
  onChange,
  options,
  disabled,
  placeholder = "Select Preparor",
}: {
  value: string;
  onChange: (id: string) => void;
  options: PreparorUser[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((u) => (u.id || u.user_id) === value);
  const selectedLabel = selected
    ? `${selected.name || ""}${selected.role ? ` (${selected.role})` : ""}`
    : "";

  const filtered = options.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q || q === selectedLabel.trim().toLowerCase()) return true;
    const name = (u.name || "").toLowerCase();
    const role = (u.role || "").toLowerCase();
    return name.includes(q) || role.includes(q);
  });

  // Keep the highlight on a valid row whenever the filter changes.
  useEffect(() => {
    setHighlightedIdx((i) =>
      filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1),
    );
  }, [filtered.length]);

  // Scroll the highlighted row into view when arrow-keying through.
  useEffect(() => {
    if (!open) return;
    const ul = listRef.current;
    if (!ul) return;
    const node = ul.querySelector<HTMLLIElement>(
      `li[data-idx="${highlightedIdx}"]`,
    );
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [highlightedIdx, open]);

  const commit = (idx: number) => {
    const opt = filtered[idx];
    if (!opt) return;
    const id = opt.id || opt.user_id || "";
    onChange(id);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightedIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      // Pick the highlighted match. preventDefault stops the surrounding
      // <form> from submitting when the user hits Enter inside the input.
      e.preventDefault();
      if (open && filtered.length > 0) {
        commit(highlightedIdx);
      } else if (!open) {
        setOpen(true);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      inputRef.current?.blur();
    }
  };

  return (
    <div className="relative">
      {/* Trigger — shows the current selection; clicking opens the picker as a
          centered popup so the list never gets clipped near the modal edges. */}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setQuery("");
          setHighlightedIdx(0);
          setOpen(true);
        }}
        className={`flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <Users className="h-3.5 w-3.5 opacity-50 shrink-0 mr-2" />
        <span className={`flex-1 min-w-0 truncate ${selectedLabel ? "" : "text-muted-foreground"}`}>
          {selectedLabel || placeholder}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear selection"
            onClick={(e) => {
              // Don't let the clear bubble up and re-open the picker.
              e.stopPropagation();
              onChange("");
              setQuery("");
            }}
            className="opacity-60 hover:opacity-100 ml-1"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className="h-4 w-4 opacity-60 ml-1 shrink-0" />
      </button>

      {/* Centered popup picker — portalled to <body> so the fixed overlay is
          centered against the viewport (the modal card's transform would
          otherwise capture position:fixed) and lands in the middle of the
          centered modal. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          onMouseDown={() => {
            setOpen(false);
            setQuery("");
          }}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Search header — full-width input, no lens icon (payroll style) */}
            <div className="flex items-center gap-2 px-2 py-2 border-b border-gray-100 bg-gray-50">
              <input
                ref={inputRef}
                autoFocus
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                autoComplete="off"
                placeholder={placeholder}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlightedIdx(0);
                }}
                onKeyDown={handleKeyDown}
                className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
              />
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  setQuery("");
                }}
                aria-label="Close"
                className="opacity-60 hover:opacity-100 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* ~5 rows visible at a time; the rest scrolls internally. */}
            <ul ref={listRef} className="max-h-[210px] overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-xs text-gray-400 italic">
                  {query
                    ? <>No users match &ldquo;{query}&rdquo;.</>
                    : "No users available."}
                </li>
              ) : (
                filtered.map((u, idx) => {
                  const id = u.id || u.user_id || "";
                  const isSelected = id === value;
                  const isHighlighted = idx === highlightedIdx;
                  return (
                    <li
                      key={id}
                      data-idx={idx}
                      onMouseEnter={() => setHighlightedIdx(idx)}
                      onMouseDown={(e) => {
                        // Prevent the input blur from closing the panel
                        // before our click-handler runs.
                        e.preventDefault();
                        commit(idx);
                      }}
                      className={`px-3 py-2 text-sm cursor-pointer ${
                        isHighlighted ? "bg-indigo-50" : "hover:bg-gray-50"
                      } ${isSelected ? "font-medium text-indigo-700" : ""}`}
                    >
                      {u.name} {u.role ? `(${u.role})` : ""}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
